import React from "react"
import styled from "styled-components"
import { Controller } from "react-hook-form"
import { InputZaverConfig, ZaverConfig } from "lib/types/generated/graphql-types"
import { Common } from "lib/types"
import { SidebarDivider } from "../../DashboardSales/Shared.styled"
import Input from "components/Ui/Form/Input"
import SingleSelect from "components/Ui/Form/SingleSelect"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"

const StyledSelect = styled(SingleSelect)`
  margin-bottom: 2rem;
`

interface ExtendedInputZaverConfig {
  id: string
  zaverConfig: InputZaverConfig
}

type Props = {
  control: Control<ExtendedInputZaverConfig>
  errors: FieldErrors
  setValue: UseFormSetValue<ExtendedInputZaverConfig>
  showId?: boolean
  defaultValues?: ZaverConfig
}

export const ZaverConfigInputs = ({
  control,
  errors,
  setValue,
  showId = true,
  defaultValues
}: Props) => {
  const updateBaseUrl = (baseUrl: Common.Option) => {
    setValue("zaverConfig.baseUrl", baseUrl.value)
  }

  const baseUrlOptions = [
    {
      value: "https://api.test.zaver.com",
      label: "Test | https://api.test.zaver.com"
    },
    {
      value: "https://api.zaver.com",
      label: "Production | https://api.zaver.com"
    }
  ]

  return (
    <>
      {showId && (
        <>
          <Controller
            name="id"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Identifier *"
                description="For internal use in Brink"
                placeholder="Aa - Zz, 0 - 9, -_."
                errors={errors}
              />
            )}
            control={control}
            defaultValue=""
            rules={{
              required: "This is a required field",
              pattern: {
                value: /^[A-Za-z0-9_.-]+$/,
                message: "Only characters Aa-Zz and 0-9 are allowed. No spaces"
              }
            }}
          />
          <SidebarDivider />
        </>
      )}
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Base URL"} value={defaultValues?.baseUrl} />}
      >
        <Controller
          name="zaverConfig.baseUrl"
          render={({ field }) => (
            <StyledSelect
              {...field}
              options={baseUrlOptions}
              setValue={updateBaseUrl}
              defaultValue={{
                label:
                  baseUrlOptions.find((option) => {
                    return option.value === defaultValues?.baseUrl?.replace(/\/$/, "")
                  })?.label ?? "Select base URL",
                value: defaultValues?.baseUrl ?? ""
              }}
              label="Base URL *"
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write}>
        <Controller
          name="zaverConfig.apiKey"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Zaver API key *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
        <Controller
          name="zaverConfig.callbackApiKey"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Zaver callback API key *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
      </WithAuth>
    </>
  )
}
